import AsyncStorage from '@react-native-async-storage/async-storage';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { getNavigationRef } from './navigationRef';

const API_URL = process.env.API_URL || 'http://localhost:4102';

// 创建 axios 实例
const httpClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求队列
const pendingRequests = new Map<string, AbortController>();

// 生成请求唯一 key
const generateRequestKey = (config: InternalAxiosRequestConfig): string => {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&');
};

// 请求拦截器
httpClient.interceptors.request.use(
  async (config) => {
    // 添加 token
    const token = await AsyncStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 防止重复请求
    const requestKey = generateRequestKey(config);
    if (pendingRequests.has(requestKey)) {
      const controller = pendingRequests.get(requestKey);
      controller?.abort();
    }

    const abortController = new AbortController();
    config.signal = abortController.signal;
    pendingRequests.set(requestKey, abortController);

    // 添加请求时间戳
    config.metadata = {
      ...config.metadata,
      startTime: Date.now(),
    };

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const { config } = response;
    const requestKey = generateRequestKey(config);

    // 移除请求队列
    pendingRequests.delete(requestKey);

    // 计算请求耗时
    if (config.metadata?.startTime) {
      const duration = Date.now() - (config.metadata.startTime as number);
      if (duration > 3000) {
        if (__DEV__) console.warn(`Slow request detected: ${config.url} took ${duration}ms`);
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const { config } = error;

    if (config) {
      const requestKey = generateRequestKey(config);
      pendingRequests.delete(requestKey);

      // 计算请求耗时
      if (config.metadata?.startTime) {
        const duration = Date.now() - (config.metadata.startTime as number);
        if (__DEV__) console.warn(`Request failed after ${duration}ms: ${config.url}`);
      }
    }

    // 处理不同错误
    if (error.response) {
      const { status, data } = error.response as AxiosResponse<APIErrorResponse>;

      // 401 未授权 - 清除 token 并跳转登录页
      if (status === 401) {
        await AsyncStorage.multiRemove(['token', 'user']);

        // Navigate to login screen using React Navigation
        const navRef = getNavigationRef();
        if (navRef && navRef.isReady()) {
          navRef.reset({
            index: 0,
            routes: [{ name: 'Login' }],
          });
        } else {
          // Fallback: wait for navigation to be ready
          setTimeout(() => {
            const retryRef = getNavigationRef();
            if (retryRef && retryRef.isReady()) {
              retryRef.reset({
                index: 0,
                routes: [{ name: 'Login' }],
              });
            }
          }, 100);
        }

        return Promise.reject({
          message: data?.message || '登录已过期，请重新登录',
          code: 401,
        });
      }

      // 403 无权限
      if (status === 403) {
        return Promise.reject({
          message: data?.message || '没有权限执行此操作',
          code: 403,
        });
      }

      // 404 资源不存在
      if (status === 404) {
        return Promise.reject({
          message: data?.message || '请求的资源不存在',
          code: 404,
        });
      }

      // 429 请求过于频繁
      if (status === 429) {
        return Promise.reject({
          message: data?.message || '请求过于频繁，请稍后再试',
          code: 429,
        });
      }

      // 500 服务器错误
      if (status >= 500) {
        return Promise.reject({
          message: data?.message || '服务器错误，请稍后再试',
          code: 500,
        });
      }

      // 其他错误
      return Promise.reject({
        message: data?.message || data?.details || '请求失败',
        code: status,
        details: data?.details,
      });
    }

    // 网络错误
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        message: '请求已取消',
        code: 0,
      });
    }

    if (error.code === 'ERR_NETWORK') {
      return Promise.reject({
        message: '网络连接失败，请检查网络设置',
        code: 0,
      });
    }

    // 超时错误
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return Promise.reject({
        message: '请求超时，请稍后再试',
        code: 0,
      });
    }

    // 未知错误
    return Promise.reject({
      message: error.message || '未知错误',
      code: 0,
    });
  },
);

// API 错误响应类型
interface APIErrorResponse {
  statusCode: number;
  code: number;
  message: string;
  details?: unknown;
  timestamp?: string;
  path?: string;
}

// 导出类型
export type { APIErrorResponse };

// 导出方法
export default httpClient;

// 导出配置类型
export type { AxiosRequestConfig, AxiosResponse };

// 工具方法：取消所有请求
export const cancelAllRequests = () => {
  pendingRequests.forEach((controller) => {
    controller.abort();
  });
  pendingRequests.clear();
};

// 工具方法：取消指定请求
export const cancelRequest = (config: InternalAxiosRequestConfig) => {
  const requestKey = generateRequestKey(config);
  const controller = pendingRequests.get(requestKey);
  if (controller) {
    controller.abort();
    pendingRequests.delete(requestKey);
  }
};
